// Mon CV
import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { User, Briefcase, GraduationCap, Award, MapPin, Phone, Mail, Plus, Edit, Trash2, Save, FileText, Camera } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Section({ title, icon: Icon, children, onAdd }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon size={16} className="text-cyan-600" />
          <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
        </div>
        {onAdd && (
          <button onClick={onAdd} className="text-cyan-600 hover:text-cyan-700">
            <Plus size={18} />
          </button>
        )}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

export default function MonCV() {
  const { user } = useCurrentUser();
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  // Données démo
  const DEMO_PROFILE = {
    firstName: 'Jean',
    lastName: 'Dupont',
    title: 'Plombier qualifié',
    email: 'jean.dupont@email.com',
    phone: '06 12 34 56 78',
    location: 'Lyon (69)',
    bio: 'Plombier passionné avec 8 ans d\'expérience. Spécialisé dans la rénovation de salles de bain et l\'installation de systèmes de chauffage.',
    disponibilite: 'immediate',
    mobilite: '30km',
  };

  const DEMO_EXPERIENCES = [];

  const DEMO_FORMATIONS = [];

  const DEMO_COMPETENCES = [];

  // Données vierges pour utilisateur réel
  const EMPTY_PROFILE = {
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    title: '',
    email: user?.email || '',
    phone: '',
    location: '',
    bio: '',
    disponibilite: '',
    mobilite: '',
  };

  const [profile, setProfile] = useState(user?.isDemo ? DEMO_PROFILE : EMPTY_PROFILE);
  const [experiences, setExperiences] = useState(user?.isDemo ? DEMO_EXPERIENCES : []);
  const [formations, setFormations] = useState(user?.isDemo ? DEMO_FORMATIONS : []);
  const [competences, setCompetences] = useState(user?.isDemo ? DEMO_COMPETENCES : []);
  const [newCompetence, setNewCompetence] = useState('');

  useEffect(() => {
    if (user) {
      if (user.isDemo) {
        setProfile(DEMO_PROFILE);
        setExperiences(DEMO_EXPERIENCES);
        setFormations(DEMO_FORMATIONS);
        setCompetences(DEMO_COMPETENCES);
      } else {
        setProfile({
          ...EMPTY_PROFILE,
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || '',
        });
        setExperiences([]);
        setFormations([]);
        setCompetences([]);
      }
    }
  }, [user]);

  // Charger la photo au montage
  useEffect(() => {
    const loadPhoto = async () => {
      if (!user?.id) return;
      try {
        const res = await fetch(`${API_URL}/api/cv/photo/${user.id}`);
        if (res.ok) {
          const data = await res.json();
          if (data.success && data.photo_url) {
            setPhotoUrl(data.photo_url);
          }
        }
      } catch (err) {
        console.error('Erreur chargement photo:', err);
      }
    };
    loadPhoto();
  }, [user?.id]);

  // Upload de photo
  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validation
    if (!file.type.startsWith('image/')) {
      toast.error('Le fichier doit être une image');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('L\'image ne doit pas dépasser 5 Mo');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API_URL}/api/cv/photo?user_id=${user?.id || 'demo'}`, {
        method: 'POST',
        body: formData,
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success && data.photo_url) {
          setPhotoUrl(data.photo_url);
          toast.success('Photo mise à jour');
        }
      } else {
        throw new Error('Erreur upload');
      }
    } catch (err) {
      console.error('Erreur upload photo:', err);
      toast.error('Erreur lors du téléchargement');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    await new Promise(r => setTimeout(r, 1000));
    setSaving(false);
    toast.success('CV enregistré');
  };

  const handleAddExperience = () => {
    const newExp = {
      id: Date.now(),
      title: '',
      company: '',
      location: '',
      startDate: '',
      endDate: '',
      description: ''
    };
    setExperiences([newExp, ...experiences]);
    setIsEditing(true);
  };

  const handleAddFormation = () => {
    const newForm = {
      id: Date.now(),
      title: '',
      school: '',
      year: ''
    };
    setFormations([newForm, ...formations]);
    setIsEditing(true);
  };

  const handleAddCompetence = () => {
    if (newCompetence.trim()) {
      setCompetences([...competences, newCompetence.trim()]);
      setNewCompetence('');
    }
  };

  const handleRemoveCompetence = (index) => {
    setCompetences(competences.filter((_, i) => i !== index));
  };

  // Calcul du taux de complétion
  const calculateCompletion = () => {
    let score = 0;
    let total = 8;
    
    if (profile.firstName) score++;
    if (profile.lastName) score++;
    if (profile.title) score++;
    if (profile.email) score++;
    if (profile.phone) score++;
    if (profile.bio) score++;
    if (experiences.length > 0) score++;
    if (competences.length > 0) score++;
    
    return Math.round((score / total) * 100);
  };

  const completionRate = calculateCompletion();

  // Si le CV est vide, afficher un état d'onboarding
  const isCVEmpty = !profile.title && experiences.length === 0 && competences.length === 0;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[16px] sm:text-xl font-bold text-[#1e3a5f]">Mon CV</h1>
          <div className="flex items-center gap-3 mt-1">
            <div className="flex items-center gap-2">
              <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-cyan-500 rounded-full transition-all"
                  style={{ width: `${completionRate}%` }}
                />
              </div>
              <span className="text-sm text-gray-500">{completionRate}% complet</span>
            </div>
          </div>
        </div>
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="bg-cyan-500 hover:bg-cyan-600 text-white"
        >
          {saving ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
              Enregistrement...
            </>
          ) : (
            <>
              <Save size={18} className="mr-2" /> Enregistrer
            </>
          )}
        </Button>
      </div>

      {/* Informations personnelles */}
      <Section title="Informations personnelles" icon={User}>
        {/* Layout responsive : colonne sur mobile, ligne sur desktop */}
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
          {/* Avatar cliquable pour upload photo - centré sur mobile */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handlePhotoChange}
            accept="image/*"
            className="hidden"
          />
          <button
            onClick={handlePhotoClick}
            disabled={uploadingPhoto}
            className="relative w-28 h-28 sm:w-24 sm:h-24 rounded-2xl overflow-hidden flex-shrink-0 group focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 transition-all hover:shadow-lg"
            title="Cliquer pour ajouter une photo"
          >
            {photoUrl ? (
              <>
                <img 
                  src={photoUrl} 
                  alt="Photo de profil" 
                  className="w-full h-full object-cover"
                />
                {/* Overlay au hover quand photo présente */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Camera size={24} className="text-white" />
                </div>
              </>
            ) : (
              /* Pas de photo : afficher directement l'icône caméra */
              <div className="w-full h-full bg-gradient-to-br from-cyan-500 to-cyan-600 flex flex-col items-center justify-center text-white gap-1">
                {uploadingPhoto ? (
                  <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Camera size={28} className="text-white" />
                    <span className="text-[10px] font-medium opacity-80">Ajouter</span>
                  </>
                )}
              </div>
            )}
          </button>
          
          {/* Champs du formulaire - pleine largeur sur mobile */}
          <div className="w-full flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Prénom</label>
              <input
                type="text"
                value={profile.firstName}
                onChange={(e) => setProfile(p => ({ ...p, firstName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Nom</label>
              <input
                type="text"
                value={profile.lastName}
                onChange={(e) => setProfile(p => ({ ...p, lastName: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Titre professionnel</label>
              <input
                type="text"
                value={profile.title}
                onChange={(e) => setProfile(p => ({ ...p, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Email</label>
              <input
                type="email"
                value={profile.email}
                onChange={(e) => setProfile(p => ({ ...p, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Téléphone</label>
              <input
                type="tel"
                value={profile.phone}
                onChange={(e) => setProfile(p => ({ ...p, phone: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Présentation</label>
              <textarea
                value={profile.bio}
                onChange={(e) => setProfile(p => ({ ...p, bio: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500 resize-none"
              />
            </div>
          </div>
        </div>
      </Section>

      {/* Expériences */}
      <Section title="Expériences professionnelles" icon={Briefcase} onAdd={handleAddExperience}>
        {experiences.length > 0 ? (
          <div className="space-y-4">
            {experiences.map(exp => (
              <div key={exp.id} className="p-4 bg-gray-50 rounded-xl">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold text-gray-900">{exp.title || 'Nouveau poste'}</h4>
                    <p className="text-cyan-600">{exp.company || 'Entreprise'} • {exp.location || 'Lieu'}</p>
                    <p className="text-sm text-gray-500">{exp.startDate || 'Début'} - {exp.endDate || 'Fin'}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button className="p-1 hover:bg-gray-200 rounded">
                      <Edit size={14} className="text-gray-500" />
                    </button>
                    <button 
                      className="p-1 hover:bg-red-100 rounded"
                      onClick={() => setExperiences(experiences.filter(e => e.id !== exp.id))}
                    >
                      <Trash2 size={14} className="text-red-500" />
                    </button>
                  </div>
                </div>
                <p className="mt-2 text-sm text-gray-600">{exp.description || 'Description...'}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Briefcase size={32} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 mb-2">Aucune expérience ajoutée</p>
            <p className="text-sm text-gray-400 mb-4">Ajoute tes expériences professionnelles</p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleAddExperience}
              className="border-cyan-500 text-cyan-600 hover:bg-cyan-50"
            >
              <Plus size={16} className="mr-2" /> Ajouter une expérience
            </Button>
          </div>
        )}
      </Section>

      {/* Formations */}
      <Section title="Formations" icon={GraduationCap} onAdd={handleAddFormation}>
        {formations.length > 0 ? (
          <div className="space-y-3">
            {formations.map(formation => (
              <div key={formation.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">{formation.title || 'Nouvelle formation'}</h4>
                  <p className="text-sm text-gray-500">{formation.school || 'École'} • {formation.year || 'Année'}</p>
                </div>
                <div className="flex items-center gap-1">
                  <button className="p-1 hover:bg-gray-200 rounded">
                    <Edit size={14} className="text-gray-500" />
                  </button>
                  <button 
                    className="p-1 hover:bg-red-100 rounded"
                    onClick={() => setFormations(formations.filter(f => f.id !== formation.id))}
                  >
                    <Trash2 size={14} className="text-red-500" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <GraduationCap size={32} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 mb-2">Aucune formation ajoutée</p>
            <p className="text-sm text-gray-400 mb-4">Ajoute tes diplômes et certifications</p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleAddFormation}
              className="border-cyan-500 text-cyan-600 hover:bg-cyan-50"
            >
              <Plus size={16} className="mr-2" /> Ajouter une formation
            </Button>
          </div>
        )}
      </Section>

      {/* Compétences */}
      <Section title="Compétences" icon={Award}>
        {competences.length > 0 ? (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {competences.map((comp, i) => (
                <span 
                  key={i}
                  className="px-3 py-1.5 bg-cyan-50 text-cyan-700 rounded-full text-sm font-medium flex items-center gap-2"
                >
                  {comp}
                  <button 
                    className="hover:text-red-500"
                    onClick={() => handleRemoveCompetence(i)}
                  >
                    <Trash2 size={12} />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newCompetence}
                onChange={(e) => setNewCompetence(e.target.value)}
                placeholder="Ajouter une compétence..."
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500 text-sm"
                onKeyPress={(e) => e.key === 'Enter' && handleAddCompetence()}
              />
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleAddCompetence}
                className="border-cyan-500 text-cyan-600"
              >
                <Plus size={16} />
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <Award size={32} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 mb-2">Aucune compétence ajoutée</p>
            <p className="text-sm text-gray-400 mb-4">Liste tes compétences clés</p>
            <div className="flex gap-2 justify-center max-w-sm mx-auto">
              <input
                type="text"
                value={newCompetence}
                onChange={(e) => setNewCompetence(e.target.value)}
                placeholder="Ex: Plomberie, Électricité..."
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500 text-sm"
                onKeyPress={(e) => e.key === 'Enter' && handleAddCompetence()}
              />
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleAddCompetence}
                className="border-cyan-500 text-cyan-600"
              >
                <Plus size={16} />
              </Button>
            </div>
          </div>
        )}
      </Section>
    </div>
  );
}
